// src/components/commercials/CommercialList.js
import React, { useState } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Tooltip,
  Chip,
  Paper,
  Stack,
  useTheme,
  TablePagination,
  useMediaQuery,
} from "@mui/material";
import {
  Edit,
  Delete,
  Search,
  Refresh,
  Person,
  Visibility,
  Business,
} from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import ConfirmDialog from "../ui/ConfirmDialog";

const CommercialList = ({ onView, onEdit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: commercials, loading } = useCollection("commercials");
  const [localCommercials, setLocalCommercials] = useState(null);
  const { remove } = useAuditFirestore("commercials");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(7);

  React.useEffect(() => {
    if (commercials) {
      setLocalCommercials(commercials);
    }
  }, [commercials]);

  React.useEffect(() => {
    setPage(0);
  }, [search]);

  const filtered = localCommercials?.filter(
    (c) =>
      c.isActive !== false &&
      (search === "" ||
        `${c.firstName || ""} ${c.lastName || ""}`
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        c.email?.toLowerCase().includes(search.toLowerCase()) ||
        c.phone?.toLowerCase().includes(search.toLowerCase()) ||
        c.companyName?.toLowerCase().includes(search.toLowerCase()))
  );

  const paginatedCommercials = filtered?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getDisplayName = (commercial) => {
    return `${commercial.firstName || ""} ${commercial.lastName || ""}`.trim() || "Sin nombre";
  };

  const getInitials = (commercial) => {
    return `${commercial.firstName?.[0] || ""}${commercial.lastName?.[0] || ""}`.toUpperCase();
  };

  if (loading || !localCommercials) return <LoadingSpinner />;

  return (
    <Box>
      {/* Barra de búsqueda */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
          borderRadius: { xs: "16px", sm: "20px" },
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "rgba(0, 95, 101, 0.12)",
          background: theme.palette.background.paper,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={1.5}
          flexWrap="wrap"
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flex={1} flexWrap="wrap">
            {/* SEARCH */}
            <TextField
              placeholder="Buscar por nombre, email o empresa"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: { xs: "100%", sm: 280, md: 360 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  background: theme.palette.background.paper,
                  height: 44,
                  display: "flex",
                  alignItems: "center",
                  "& fieldset": {
                    borderColor: theme.palette.divider,
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.primary.main,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search
                      fontSize="small"
                      sx={{ color: theme.palette.primary.main }}
                    />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch("")}>
                      ✖
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          {/* TOTAL */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`Total: ${filtered?.length || 0}`}
              sx={{
                height: 44,
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                background: theme.palette.background.paper,
                border: "1px solid",
                borderColor: theme.palette.info.main,
                color: theme.palette.info.main,
              }}
            />

            <Tooltip title="Refrescar">
              <IconButton
                onClick={() => {
                  if (commercials) setLocalCommercials(commercials);
                }}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(0, 175, 163, 0.1)"
                        : "rgba(0, 95, 101, 0.05)",
                  },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {filtered?.length === 0 ? (
        <EmptyState
          title={search ? "Sin resultados" : "Sin comerciales"}
          description={
            search
              ? "Prueba con otros filtros"
              : "Añade tu primer comercial"
          }
          actionLabel="Nuevo Comercial"
          onAction={() => onEdit(null)}
        />
      ) : (
        <Card
          elevation={2}
          sx={{
            borderRadius: { xs: 1, sm: 2 },
            overflow: "hidden",
            border: "1px solid",
            borderColor: theme.palette.divider,
            background: theme.palette.background.paper,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(0, 175, 163, 0.1)"
                        : "rgba(0, 95, 101, 0.05)",
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      width: { xs: 50, sm: 60 },
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    {isMobile ? "" : "Avatar"}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                  >
                    Comercial
                  </TableCell>
                  {!isMobile && (
                    <>
                      <TableCell
                        sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}
                      >
                        Email
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}
                      >
                        Teléfono
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}
                      >
                        Empresa
                      </TableCell>
                    </>
                  )}
                  <TableCell
                    sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                  >
                    Porcentaje
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      width: { xs: 100, sm: 130 },
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCommercials?.map((commercial) => (
                  <TableRow
                    key={commercial.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0, 95, 101, 0.02)",
                      },
                    }}
                  >
                    <TableCell onClick={() => onView(commercial)}>
                      <Avatar
                        sx={{
                          width: { xs: 32, sm: 40 },
                          height: { xs: 32, sm: 40 },
                          background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                          fontSize: { xs: "0.8rem", sm: "1rem" },
                          fontWeight: 500,
                        }}
                      >
                        {getInitials(commercial)}
                      </Avatar>
                    </TableCell>
                    <TableCell onClick={() => onView(commercial)}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                        {getDisplayName(commercial)}
                      </Typography>
                      {isMobile && (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                            {commercial.email || "—"}
                          </Typography>
                          {commercial.companyName && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", display: "block" }}>
                              {commercial.companyName}
                            </Typography>
                          )}
                        </>
                      )}
                    </TableCell>
                    {!isMobile && (
                      <>
                        <TableCell onClick={() => onView(commercial)}>
                          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                            {commercial.email || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell onClick={() => onView(commercial)}>
                          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                            {commercial.phone || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell onClick={() => onView(commercial)}>
                          <Chip
                            size="small"
                            icon={<Business sx={{ fontSize: 14 }} />}
                            label={commercial.companyName || "—"}
                            variant="outlined"
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.75rem" },
                              maxWidth: 200,
                            }}
                          />
                        </TableCell>
                      </>
                    )}
                    <TableCell onClick={() => onView(commercial)}>
                      <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                        {commercial.percentage || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        <Tooltip title="Ver detalle">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onView(commercial);
                            }}
                            sx={{ color: theme.palette.info.main }}
                          >
                            <Visibility sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(commercial);
                            }}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <Edit sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(commercial);
                            }}
                            sx={{ color: theme.palette.error.main }}
                          >
                            <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Paginación */}
          <TablePagination
            component="div"
            count={filtered?.length || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[7, 10, 25, 50]}
            labelRowsPerPage={isMobile ? "Filas:" : "Filas por página:"}
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} de ${count}`
            }
            sx={{
              borderTop: "1px solid",
              borderColor: theme.palette.divider,
              "& .MuiTablePagination-toolbar": {
                minHeight: 48,
                px: { xs: 1, sm: 2 },
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: { xs: "0.7rem", sm: "0.85rem" },
              },
            }}
          />
        </Card>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          setLocalCommercials((prev) =>
            prev.filter((c) => c.id !== deleteTarget.id),
          );
          const deletedCommercial = deleteTarget;
          setDeleteTarget(null);

          try {
            await remove(deletedCommercial.id);
          } catch (error) {
            console.error("Error deleting:", error);
            setLocalCommercials((prev) => [...prev, deletedCommercial]);
          }
        }}
        title="Eliminar Comercial"
        message={`¿Eliminar a ${getDisplayName(deleteTarget || {})}?`}
        type="delete"
      />
    </Box>
  );
};

export default CommercialList;